// src/ErrorBoundary.jsx
// Without this, any uncaught render error anywhere in the tree unmounts the
// whole app and leaves a blank white screen with zero indication of why —
// which is exactly what's been reported both at app launch and when opening
// a stash item. This turns that into a visible, readable error instead,
// and lets the user recover without force-quitting/reinstalling.
import React from 'react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Uncaught render error:', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const details = `${error?.name || 'Error'}: ${error?.message || String(error)}\n\n${error?.stack || ''}`;

    return (
      <div style={{
        minHeight: '100dvh', width: '100%', boxSizing: 'border-box',
        padding: 24, background: '#f5f0eb', color: '#1c1917',
        fontFamily: "-apple-system,'Segoe UI',sans-serif",
        display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div>
          <p style={{ fontSize: 28, margin: '0 0 8px' }}>⚠️</p>
          <p style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px' }}>Something broke</p>
          <p style={{ fontSize: 13, color: '#57534e', margin: 0 }}>
            Your data is safe on this device — this is just a display crash. The details below help fix it.
          </p>
        </div>
        <pre style={{
          fontSize: 11, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          background: '#fdf9f5', border: '1px solid #ddd5c8', borderRadius: 8,
          padding: 12, margin: 0, flex: 1, overflow: 'auto',
        }}>{details}</pre>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => window.location.reload()}
            style={{ flex: 1, padding: '10px 0', background: '#9a3412', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
          >Reload</button>
          {navigator.clipboard && (
            <button
              onClick={() => navigator.clipboard.writeText(details).catch(() => {})}
              style={{ flex: 1, padding: '10px 0', background: 'none', border: '1px solid #ddd5c8', borderRadius: 8, color: '#57534e', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            >Copy error</button>
          )}
        </div>
      </div>
    );
  }
}
